"use client";

import clsx from "clsx";
import { default as Image } from "next/image";
import { useState } from "react";
import Chevron from "@/components/icons/Chevron";
import ChevronLeft from "@/components/icons/ChevronLeft";
import styles from "./product-card-carousel.module.css";

interface ProductImage {
  href: string;
  alt: string;
}

interface ProductCardCarouselProps {
  images: ProductImage[];
  productName: string;
}

export const ProductCardCarousel = ({
  images,
  productName,
}: ProductCardCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const validImages = images.filter(Boolean);

  if (validImages.length === 0) {
    return null;
  }

  const showNavigation = validImages.length > 1;

  const minSwipeDistance = 50;

  const handlePrevious = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      setCurrentIndex((prev) =>
        prev === validImages.length - 1 ? 0 : prev + 1
      );
    }

    if (isRightSwipe) {
      setCurrentIndex((prev) =>
        prev === 0 ? validImages.length - 1 : prev - 1
      );
    }
  };

  return (
    <div
      className={styles.carouselContainer}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {validImages.map((image, index) => (
        <Image
          key={index}
          src={image.href}
          alt={image.alt || productName}
          fill
          quality={80}
          loading="eager"
          sizes={`(max-width: 1024px) 100vw, (max-width: 2560px) 30vw`}
          className={clsx(
            styles.carouselImage,
            index === currentIndex && styles.active
          )}
        />
      ))}

      {showNavigation && (
        <>
          <button
            onClick={handlePrevious}
            className={clsx(styles.navButton, styles.prevButton)}
            aria-label="Previous image"
            type="button"
          >
            <Chevron scale={1.5} />
          </button>

          <button
            onClick={handleNext}
            className={clsx(styles.navButton, styles.nextButton)}
            aria-label="Next image"
            type="button"
          >
            <ChevronLeft scale={1.5} />
          </button>
        </>
      )}
    </div>
  );
};
